///
/// EarthBrowser
///
/// Created by Matt Giger on 10/30/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The user-facing satellite sets: ten curated, multi-selectable groupings
/// over the ~48 raw CelesTrak groups the TLE feed carries (each TLE row has
/// its group_ids). Shared by the satellite layer (which satellites to load),
/// the Layers panel chip strip (what to offer) and redux validation (what a
/// persisted selection may contain). Groups overlap, so a set's satellites
/// are the union of its groups de-duplicated by object id.
///

import { type SatelliteArchetype } from "./satelliteArchetype"

export interface SatelliteSet {
	id: string
	/** Full name for tooltips, legend rows and the data browser status */
	name: string
	/** Chip label */
	short: string
	/** CelesTrak group ids whose members make up the set */
	groupIds: readonly string[]
	/** Representative archetype for the set's glyph */
	archetype: SatelliteArchetype
	/** Dominant orbit regime, for the chip's tint dot */
	orbit: "LEO" | "MEO" | "GEO" | "mixed"
	/**
	 * Thousands of objects: shown with a warning tint so the cost of
	 * turning it on is visible before the globe fills up
	 */
	heavy?: boolean
}

export const cSatelliteSets: readonly SatelliteSet[] = [
	{ id: "brightest", name: "Brightest", short: "Brightest", groupIds: ["visual"], archetype: "wings", orbit: "mixed" },
	{ id: "stations", name: "Space stations", short: "Stations", groupIds: ["stations"], archetype: "truss", orbit: "LEO" },
	{ id: "weather", name: "Weather", short: "Weather", groupIds: ["weather", "goes", "sarsat", "argos"], archetype: "dish", orbit: "mixed" },
	{ id: "earth-obs", name: "Earth observation & science", short: "Earth obs", groupIds: ["resource", "science", "planet", "spire", "dmc", "geodetic"], archetype: "wings", orbit: "LEO" },
	{ id: "navigation", name: "Navigation", short: "Nav", groupIds: ["gnss", "gps-ops", "glo-ops", "galileo", "beidou", "sbas", "nnss", "musson"], archetype: "nav", orbit: "MEO" },
	{ id: "comms", name: "Communications (GEO)", short: "Comms", groupIds: ["geo", "ses", "intelsat", "eutelsat", "tdrss", "other-comm", "x-comm"], archetype: "dish", orbit: "GEO" },
	{ id: "constellations", name: "Broadband constellations", short: "Constellations", groupIds: ["starlink", "oneweb", "iridium-NEXT", "globalstar", "orbcomm"], archetype: "flat", orbit: "LEO", heavy: true },
	{ id: "amateur", name: "Amateur & CubeSats", short: "CubeSats", groupIds: ["amateur", "cubesat", "satnogs", "education"], archetype: "cube", orbit: "LEO" },
	{ id: "recent", name: "Launched in the last 30 days", short: "Recent", groupIds: ["last-30-days"], archetype: "wings", orbit: "mixed" },
	{ id: "debris", name: "Debris", short: "Debris", groupIds: ["fengyun-1c-debris", "cosmos-2251-debris", "iridium-33-debris", "cosmos-1408-debris"], archetype: "shard", orbit: "LEO", heavy: true },
]

export const cDefaultSatelliteSets: readonly string[] = ["brightest"]

const cSetIds = new Set(cSatelliteSets.map(s => s.id))

export function isSatelliteSetId(id: unknown): id is string {
	return typeof id === "string" && cSetIds.has(id)
}

/** Group ids covered by the given sets, de-duplicated, in set order */
export function groupIdsForSets(setIds: readonly string[]): string[] {
	const out: string[] = []
	for (const set of cSatelliteSets) {
		if (!setIds.includes(set.id)) continue
		for (const groupId of set.groupIds) {
			if (!out.includes(groupId)) out.push(groupId)
		}
	}
	return out
}

/** The set a raw CelesTrak group belongs to, if any */
export function setForGroupId(groupId: string): SatelliteSet | undefined {
	return cSatelliteSets.find(set => set.groupIds.includes(groupId))
}
